import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { DataTable } from "@/components/tables/DataTable";
import { ConfirmDialog } from "@/components/modals/ConfirmDialog";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { toast } from "@/store/toast.store";
import { getApiErrorMessage } from "@/api/client";
import { formatDate } from "@/utils/format";
import { useAuthUser } from "@/features/auth/useAuth";
import type { AppKind, AppVersionInput, AppVersionRelease } from "@/api/appVersions.api";
import { APP_KINDS, APP_LABEL } from "@/features/appVersions/types";
import { buildUpdatePayload, type PolicyFormValues } from "@/features/appVersions/policyForm";
import { useAppVersionHistory, useAppVersionsList, useUpdateAppVersion } from "@/features/appVersions/useAppVersions";
import { PolicyCard } from "./PolicyCard";
import { PolicyModal } from "./PolicyModal";
import { ReleaseModal, type ReleaseFormValues } from "./ReleaseModal";

export default function AppVersions() {
  const versions = useAppVersionsList();
  const update = useUpdateAppVersion();
  const { hasRole } = useAuthUser();
  // Matches the API: only ADMIN / OPERATIONS (and SUPER_ADMIN) may change a version.
  const canEdit = hasRole("ADMIN", "OPERATIONS");

  const [app, setApp] = useState<AppKind>("RIDER");
  const history = useAppVersionHistory(app);
  const [editing, setEditing] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [pending, setPending] = useState<AppVersionInput | null>(null);

  const policy = versions.data?.find((v) => v.app === app);
  const latest = policy?.latestBuildNumber ?? null;

  function save(payload: AppVersionInput) {
    update.mutate(
      { app, ...payload },
      {
        onSuccess: () => {
          toast.success(`${APP_LABEL[app]} set to version ${payload.latestBuildNumber}`);
          setEditing(false);
          setReleaseOpen(false);
          setPending(null);
        },
        onError: (err) => {
          setPending(null);
          toast.error(getApiErrorMessage(err));
        },
      },
    );
  }

  /** Saves straight away, unless it starts or re-triggers a forced update — then confirm first. */
  function requestSave(payload: AppVersionInput) {
    const startsBlocking = payload.forceUpdate && (!policy?.forceUpdate || payload.latestBuildNumber > (latest ?? 0));
    if (startsBlocking) setPending(payload);
    else save(payload);
  }

  function onPolicySubmit(values: PolicyFormValues) {
    requestSave(buildUpdatePayload(values));
  }

  function onReleaseSubmit(values: ReleaseFormValues) {
    // Store links are left out on purpose: the server keeps the ones already saved.
    requestSave({
      latestBuildNumber: values.version,
      forceUpdate: values.forceUpdate,
      releaseNotes: values.notes,
    });
  }

  const columns = useMemo<ColumnDef<AppVersionRelease, unknown>[]>(
    () => [
      {
        header: "Version",
        cell: ({ row }) => <span className="font-mono font-semibold text-ink">{row.original.buildNumber}</span>,
      },
      { header: "Released", cell: ({ row }) => formatDate(row.original.releasedAt) },
      {
        header: "Release notes",
        cell: ({ row }) => <span className="line-clamp-2 max-w-md text-ink-muted">{row.original.releaseNotes || "—"}</span>,
      },
      {
        header: "Status",
        cell: ({ row }) =>
          row.original.buildNumber === latest ? <Badge tone="teal">Latest</Badge> : <Badge tone="neutral">Previous</Badge>,
      },
    ],
    [latest],
  );

  if (versions.isLoading) return <LoadingState label="Loading app versions…" />;
  if (versions.error) return <ErrorState error={versions.error} onRetry={() => versions.refetch()} />;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          size="md"
          options={APP_KINDS.map((k) => ({ value: k, label: APP_LABEL[k] }))}
          value={app}
          onChange={setApp}
        />
        {canEdit && (
          <Button size="sm" onClick={() => setReleaseOpen(true)}>
            <Plus className="h-4 w-4" /> Register release
          </Button>
        )}
      </div>

      {policy && <PolicyCard policy={policy} canEdit={canEdit} onEdit={() => setEditing(true)} />}

      <section className="card">
        <div className="border-b border-border px-5 py-3">
          <h2 className="font-semibold text-ink">Release history</h2>
        </div>
        <DataTable
          columns={columns}
          data={history.data ?? []}
          isLoading={history.isLoading}
          error={history.error}
          onRetry={() => history.refetch()}
          rowKey={(r) => r.id}
          emptyTitle="No releases registered"
        />
      </section>

      <PolicyModal
        open={editing}
        app={app}
        current={policy}
        saving={update.isPending}
        onClose={() => setEditing(false)}
        onSubmit={onPolicySubmit}
      />
      <ReleaseModal
        open={releaseOpen}
        app={app}
        currentVersion={latest}
        saving={update.isPending}
        onClose={() => setReleaseOpen(false)}
        onSubmit={onReleaseSubmit}
      />

      <ConfirmDialog
        open={pending !== null}
        title="Force this update?"
        confirmLabel="Force update"
        loading={update.isPending}
        description="Everyone on an older version will see a full-screen update screen and can't use the app until they update from the store. Make sure the new version is live in both stores first."
        onConfirm={() => pending && save(pending)}
        onCancel={() => setPending(null)}
      >
        {pending && (
          <div className="flex justify-between rounded-lg border border-danger-100 bg-danger-50 p-3 text-sm text-ink">
            <span>{APP_LABEL[app]} · Android & iOS</span>
            <span className="font-mono font-semibold">version {pending.latestBuildNumber} required</span>
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
